import { id, type InstaQLEntity } from "@instantdb/react";
import { effect } from "@preact/signals";
import { empty, join, map, nonempty, pipe, sortKey } from "@uri/gamla";
import parseHtmlToReact, { domToReact } from "html-react-parser";
import { CSSProperties } from "preact";
import {
  type FC,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "preact/compat";
import { toast } from "react-hot-toast";
import { extractErrorMessage, notifyError } from "./notify.ts";
import { ClipLoader } from "react-spinners";
import { getApiBase } from "./config.ts";
import { LiveTimeAgo } from "./uiComponents.tsx";

import {
  type ConversationWithParticipants,
  queryOnce,
  transact,
  tx,
  useAuth,
  useQuery,
} from "./db.ts";
import { convos, selectedConversation, selectedGroup } from "./filters.tsx";
import {
  AlertCircle,
  ChevronDown as LucideChevronDown,
  Copy,
  Funnel,
  Link,
  RefreshCw,
} from "lucide-preact";
import schema from "../../instant.schema.ts";
import { sanitizeBrokenHtml } from "./sanitizeBrokenHtml.ts";
import { trimUrlTrailingJunk } from "./linkify.ts";
import "./style.css";
import { useLocation } from "preact-iso";
import { updateUrlParams } from "./urlParams.ts";
import {
  ErrorMessage,
  InvalidIdError,
  isUuid,
  Loading,
  looksLikeId,
  ObfuscatedText,
  obfuscateIdsSignal,
  PageContainer,
  useIsMobile,
} from "./uiComponents.tsx";
import { conversationLink } from "./urlParams.ts";
import {
  participantColor,
  primaryColor,
  resetParticipantColors,
  t,
} from "./theme.ts";
import { formatElapsed, normalizeTimestamp } from "../../src/timeUtils.ts";

const getConvoIdFromUrl = () => {
  if (typeof location === "undefined") return null;
  const params = new URLSearchParams(location.search);
  const id = params.get("convo");
  if (!id) return null;
  return decodeURIComponent(id).replace(/[^a-zA-Z0-9-]/g, "");
};

const syncSelectedConversationFromUrl = () => {
  const id = getConvoIdFromUrl();
  if (id && selectedConversation.value !== id) selectedConversation.value = id;
};

if (typeof window !== "undefined") {
  syncSelectedConversationFromUrl();

  globalThis.addEventListener("popstate", () => {
    selectedConversation.value = getConvoIdFromUrl();
  });

  effect(() => {
    const id = selectedConversation.value;
    const currentId = getConvoIdFromUrl();
    const url = new URL(location.href);
    if (id && id !== currentId) {
      url.searchParams.set("convo", encodeURIComponent(id));
      history.pushState(null, "", url.pathname + url.search);
    } else if (!id && currentId) {
      url.searchParams.delete("convo");
      history.pushState(null, "", url.pathname + url.search);
    }
  });
}

const formatTimestamp = (epochMs: number): string => {
  const normMs = normalizeTimestamp(epochMs);
  if (normMs <= 0) return "-";
  const date = new Date(normMs);
  const options: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return date.toLocaleString("en-US", options);
};
type ConversationEvent = InstaQLEntity<typeof schema, "messages">;

const conversationToText = pipe(
  map(
    ({ from, time, text }: ConversationEvent) =>
      `[${formatTimestamp(time)}] ${from}: ${text}`,
  ),
  join("\n"),
);

// CSS class for linkified anchors
const LINK_CLASS = "message-link";

// Strictly sanitize potentially dangerous HTML while preserving common markup.
// Removes scripts and active content, strips event handlers, and normalizes links.
export const parseAndModifyHtml = (htmlText: string) => {
  const sanitized = sanitizeBrokenHtml(htmlText);

  // Normalize self-closing <audio> tags to explicitly closed <audio></audio> tags.
  // HTML5 parser ignores self-closing slashes on non-void elements and would otherwise
  // treat all subsequent sibling elements as fallback content inside the <audio> tag.
  const closedTags = sanitized
    .replace(
      /<audio\s*([^>]*?)\s*\/?>/gi,
      (match, attrs) => {
        if (match.endsWith("/>")) {
          return `<audio ${attrs.trim()}></audio>`;
        }
        return match;
      },
    )
    .replace(
      /<button\s*([^>]*?)\s*\/?>/gi,
      (match, attrs) => {
        if (match.endsWith("/>")) {
          return `<button ${attrs.trim()}></button>`;
        }
        return match;
      },
    );

  const doc = new DOMParser().parseFromString(closedTags, "text/html");

  // 1) Replace outright dangerous nodes with their esc                                                                                                                                                                                                                                                                                                                                                                                                     aped literal markup so they render as text
  const dangerousTags = [
    "script",
    "iframe",
    "object",
    "embed",
    "link",
    "meta",
    "base",
    "style",
  ];
  Array.from(doc.querySelectorAll(dangerousTags.join(", "))).forEach((n) => {
    const html = (n as HTMLElement).outerHTML ?? "";
    // Insert raw markup as text; it will be escaped once by serializer
    const textNode = doc.createTextNode(html);
    n.replaceWith(textNode);
  });

  // 2) Scrub attributes on all elements
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
  let current = walker.currentNode as Element | null;
  const isUnsafeProtocol = (url: string) => {
    const v = url.trim().toLowerCase();
    // Block javascript: and other non-http(s)/mailto/tel protocols
    if (v.startsWith("javascript:")) return true;
    if (v.startsWith("vbscript:")) return true;
    if (v.startsWith("data:")) {
      // Allow only data:image/* to avoid script/data HTML payloads
      return !v.startsWith("data:image/");
    }
    // Allow http, https, mailto, tel, and relative URLs
    return false;
  };
  while (current) {
    // Clone NamedNodeMap to static array before mutation
    for (const attr of Array.from(current.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value;
      // Remove attributes with invalid names to prevent Preact from throwing InvalidCharacterError.
      // Valid HTML attribute names cannot contain spaces, quotes, '=', '>', '/', or control characters.
      if (/[\s"'`=/>]/.test(attr.name)) {
        try {
          current.removeAttributeNode(attr);
        } catch (_) {
          try {
            current.removeAttribute(attr.name);
          } catch (_) {
            // ignore
          }
        }
        continue;
      }
      // Remove inline event handlers like onclick, onerror, etc.
      if (name.startsWith("on")) {
        current.removeAttribute(attr.name);
        continue;
      }
      // Sanitize style with legacy expression() or javascript URLs
      if (name === "style") {
        const v = value.toLowerCase();
        if (
          v.includes("expression(") ||
          v.includes("url(javascript:") ||
          v.includes('url("javascript:') ||
          v.includes("url('javascript:")
        ) {
          current.removeAttribute(attr.name);
          continue;
        }
      }
      // Neutralize dangerous href/src values
      if (name === "href" || name === "src" || name === "xlink:href") {
        if (isUnsafeProtocol(value)) {
          current.removeAttribute(attr.name);
          continue;
        }
      }
    }

    const tagName = current.tagName.toLowerCase();
    const blockTags = [
      "p",
      "div",
      "li",
      "blockquote",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
    ];
    if (blockTags.includes(tagName) && !current.hasAttribute("dir")) {
      current.setAttribute("dir", "auto");
    } else if (tagName === "pre") {
      current.setAttribute("dir", "ltr");
    }

    // Normalize anchors: enforce rel noopener and target _blank for external links
    if (tagName === "a") {
      const a = current as HTMLAnchorElement;
      a.classList.add(LINK_CLASS);
      const href = a.getAttribute("href") ?? "";
      if (href.startsWith("http://") || href.startsWith("https://")) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    }

    current = walker.nextNode() as Element | null;
  }

  // 3) Make videos lighter by default
  doc.querySelectorAll("video").forEach((video) => {
    video.setAttribute("preload", "none");
  });

  // 4) Linkify plain URLs that appear in text nodes (not inside attributes)
  // This avoids corrupting markup like src="https://..." by only operating on text content.
  const isInsideAnchor = (el: Element | null) => {
    while (el) {
      if (el.tagName && el.tagName.toLowerCase() === "a") return true;
      el = el.parentElement;
    }
    return false;
  };
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g;
  const textWalker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let textNode = textWalker.nextNode() as Text | null;
  while (textNode) {
    const parent = textNode.parentElement;
    const text = textNode.nodeValue ?? "";
    if (text && parent && !isInsideAnchor(parent) && urlRegex.test(text)) {
      urlRegex.lastIndex = 0; // reset for reuse
      const frag = doc.createDocumentFragment();
      let lastIdx = 0;
      let match: RegExpExecArray | null;
      while ((match = urlRegex.exec(text)) !== null) {
        const matched = match[0];
        const url = trimUrlTrailingJunk(matched);
        // Append text before the URL
        if (match.index > lastIdx) {
          frag.appendChild(
            doc.createTextNode(text.slice(lastIdx, match.index)),
          );
        }
        // Create anchor
        const a = doc.createElement("a");
        a.setAttribute("href", url);
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
        a.classList.add(LINK_CLASS);
        a.textContent = url;
        frag.appendChild(a);
        // Keep stripped trailing characters (e.g. a closing bracket) as plain text
        const trailing = matched.slice(url.length);
        if (trailing) {
          frag.appendChild(doc.createTextNode(trailing));
        }
        lastIdx = match.index + matched.length;
      }
      // Append remaining text
      if (lastIdx < text.length) {
        frag.appendChild(doc.createTextNode(text.slice(lastIdx)));
      }
      // Replace the original text node
      textNode.replaceWith(frag);
    }
    textNode = textWalker.nextNode() as Text | null;
  }

  // 5) In light mode, strip dark background colors from code/pre blocks
  //    (e.g. syntax-highlighted HTML with inline hljs backgrounds baked in)
  if (!isDarkMode.value) {
    doc.querySelectorAll("pre, code").forEach((block) => {
      const stripBg = (el: Element) => {
        const style = (el as HTMLElement).style;
        if (style?.backgroundColor) style.backgroundColor = "";
        if (style?.background) style.background = "";
      };
      stripBg(block);
      block.querySelectorAll("*").forEach(stripBg);
    });
  }

  return doc.body.innerHTML;
};

const VoiceNotePlayer: FC<{
  src?: string;
  text?: string;
  lang?: string;
  gender?: string;
}> = ({ src, text, lang, gender }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (src) {
      const audio = new Audio(src);
      audioRef.current = audio;

      const onTimeUpdate = () => setCurrentTime(audio.currentTime);
      const onLoadedMetadata = () => setDuration(audio.duration || 0);
      const onEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener("timeupdate", onTimeUpdate);
      audio.addEventListener("loadedmetadata", onLoadedMetadata);
      audio.addEventListener("ended", onEnded);

      return () => {
        audio.pause();
        audio.removeEventListener("timeupdate", onTimeUpdate);
        audio.removeEventListener("loadedmetadata", onLoadedMetadata);
        audio.removeEventListener("ended", onEnded);
      };
    }
  }, [src]);

  // deno-lint-ignore no-explicit-any
  const handlePlayPause = (e: any) => {
    e.stopPropagation();
    if (src && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        if (typeof window !== "undefined" && globalThis.speechSynthesis) {
          globalThis.speechSynthesis.cancel();
        }
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch((err) => {
            notifyError(err, "Failed to play audio");
          });
      }
    } else if (text) {
      if (typeof window === "undefined" || !globalThis.speechSynthesis) {
        toast.error("Speech synthesis not supported in this browser");
        return;
      }

      if (isPlaying) {
        globalThis.speechSynthesis.cancel();
        setIsPlaying(false);
      } else {
        globalThis.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        if (lang) utterance.lang = lang;

        // Try to match voices
        const voices = globalThis.speechSynthesis.getVoices();
        const matchingVoice = voices.find((v) => {
          const lMatch = lang
            ? v.lang.toLowerCase().startsWith(lang.toLowerCase())
            : true;
          const gMatch = gender
            ? gender.toLowerCase() === "female"
              ? v.name.toLowerCase().includes("female") ||
                v.name.toLowerCase().includes("zira") ||
                v.name.toLowerCase().includes("samantha") ||
                v.name.toLowerCase().includes("hebrew")
              : v.name.toLowerCase().includes("male") ||
                v.name.toLowerCase().includes("david")
            : true;
          return lMatch && gMatch;
        }) || voices.find((v) =>
          lang ? v.lang.toLowerCase().startsWith(lang.toLowerCase()) : false
        );

        if (matchingVoice) utterance.voice = matchingVoice;

        utterance.onend = () => {
          setIsPlaying(false);
        };
        utterance.onerror = () => {
          setIsPlaying(false);
        };

        globalThis.speechSynthesis.speak(utterance);
        setIsPlaying(true);
      }
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || !isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        gap: 8,
        padding: "8px 12px",
        borderRadius: 12,
        background: "rgba(0, 0, 0, 0.15)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        margin: "8px 0",
        minWidth: 260,
        maxWidth: "100%",
        color: "inherit",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          userSelect: "none",
        }}
      >
        <button
          type="button"
          onClick={handlePlayPause}
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.25)",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "currentColor",
            outline: "none",
            flexShrink: 0,
            transition: "transform 0.1s, background 0.15s",
          }}
        >
          {isPlaying
            ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            )
            : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={{ marginLeft: 2 }}
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
        </button>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <span>🎙️ {src ? "Voice Note" : "Simulated TTS"}</span>
            {gender && (
              <span
                style={{
                  fontSize: 10,
                  opacity: 0.6,
                  textTransform: "capitalize",
                }}
              >
                ({gender})
              </span>
            )}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
            }}
          >
            <div
              style={{
                flex: 1,
                height: 4,
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: 2,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${
                    duration > 0
                      ? (currentTime / duration) * 100
                      : isPlaying
                      ? 100
                      : 0
                  }%`,
                  background: "currentColor",
                  borderRadius: 2,
                }}
              />
            </div>
            <span
              style={{
                fontSize: 11,
                opacity: 0.7,
                fontFamily: "monospace",
                flexShrink: 0,
              }}
            >
              {src
                ? `${formatTime(currentTime)} / ${formatTime(duration)}`
                : isPlaying
                ? "Playing"
                : "0:00"}
            </span>
          </div>
          {!src && (
            <span
              style={{
                fontSize: 9,
                opacity: 0.5,
                marginTop: 4,
                display: "block",
              }}
            >
              *Simulated locally via your browser; original voice may differ.
            </span>
          )}
        </div>
      </div>

      {text && (
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.4,
            padding: "8px 12px",
            borderRadius: 8,
            background: "rgba(0, 0, 0, 0.08)",
            borderLeft: "3px solid currentColor",
            fontStyle: "italic",
            marginTop: 4,
            wordBreak: "break-word",
            userSelect: "text",
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

const parseHtmlToReactWithCatch = (htmlText: string) => {
  try {
    const html = parseAndModifyHtml(htmlText);
    // deno-lint-ignore no-explicit-any
    return (parseHtmlToReact as any)(html, {
      // deno-lint-ignore no-explicit-any
      replace: (domNode: any) => {
        if (domNode.name === "audio") {
          const src = domNode.attribs?.src;
          const tts = domNode.attribs?.tts;
          const lang = domNode.attribs?.lang;
          const gender = domNode.attribs?.gender;

          let textContent = "";
          if (domNode.children && domNode.children.length > 0) {
            // deno-lint-ignore no-explicit-any
            const childrenList: any[] = domNode.children || [];
            textContent = childrenList
              .filter((c) => c.type === "text")
              .map((c) => c.data)
              .join("")
              .trim();
          }

          return (
            <VoiceNotePlayer
              src={src}
              text={tts || textContent}
              lang={lang}
              gender={gender}
            />
          );
        }
        if (domNode.name === "button") {
          let textContent = "";
          if (domNode.children && domNode.children.length > 0) {
            // deno-lint-ignore no-explicit-any
            const childrenList: any[] = domNode.children || [];
            textContent = childrenList
              .filter((c) => c.type === "text")
              .map((c) => c.data)
              .join("")
              .trim();
          }
          if (!textContent && domNode.attribs?.title) {
            textContent = domNode.attribs.title.trim();
          }
          return (
            <span className="chat-quick-reply-button">
              {textContent}
            </span>
          );
        }
        if (domNode.name === "buttons") {
          return (
            <div className="chat-quick-replies-container">
              {/* deno-lint-ignore no-explicit-any */}
              {(domToReact as any)(domNode.children)}
            </div>
          );
        }
        return undefined;
      },
    }) as ReactElement;
  } catch (_) {
    console.error("Error parsing HTML:", htmlText);
    return htmlText as unknown as ReactElement;
  }
};

import { isDarkMode } from "./routes.tsx";

type Message = InstaQLEntity<typeof schema, "messages">;

const MessageBubble: FC<{
  id: string;
  text: string;
  time: number;
  align: "flex-start" | "flex-end";
  from: string;
  allParticipants: string[];
  translatedText?: string;
  conversationId: string;
}> = ({
  id: _id,
  text,
  align,
  from,
  allParticipants,
  translatedText,
  conversationId,
}) => {
  const maxLines = 10;
  const isTruncatedInitial = useMemo(() => {
    if (text.includes("<video")) return false;
    const plainText = text.replace(/<[^>]+>/g, "");
    return plainText.split("\n").length > maxLines;
  }, [text]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(isTruncatedInitial);
  const messageRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const pressTimer = useRef<number | null>(null);
  const th = t();

  useEffect(() => {
    setIsTruncated(isTruncatedInitial);
  }, [isTruncatedInitial]);

  // Get color using hash-based participant color
  const color = participantColor(from, allParticipants);
  const bubbleBg = color.bg;
  const bubbleColor = color.text;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Bubble text copied!"),
      () => toast.error("Failed to copy text"),
    );
  };

  const copyLink = () => {
    const url = `${conversationLink(conversationId)}#${_id}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link to message copied!"),
      () => toast.error("Failed to copy link"),
    );
  };

  const handleTouchStart = () => {
    setPressing(true);
    pressTimer.current = setTimeout(() => {
      setPressing(false);
      setShowActionSheet(true);
    }, 600);
  };

  const handleTouchEnd = () => {
    setPressing(false);
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const isLeft = align === "flex-start";

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        handleTouchEnd();
      }}
      style={{
        display: "flex",
        flexDirection: isLeft ? "row" : "row-reverse",
        alignItems: "flex-end",
        gap: 4,
        alignSelf: align,
        maxWidth: "85%",
      }}
    >
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchEnd}
        onTouchEnd={handleTouchEnd}
        style={{
          flexDirection: "column",
          alignItems: align,
          backgroundColor: bubbleBg,
          color: bubbleColor,
          borderRadius: 18,
          borderTopLeftRadius: isLeft ? 4 : 18,
          borderTopRightRadius: isLeft ? 18 : 4,
          minWidth: 40,
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          display: "flex",
          position: "relative",
          overflow: "hidden",
          flex: "0 1 auto",
          minHeight: 0,
        }}
      >
        {pressing && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle, rgba(255,255,255,0.18), rgba(255,255,255,0.06))",
              opacity: 0,
              animation: "bubble-glare 600ms ease-out forwards",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}
        <div
          style={{
            position: "relative",
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            ref={messageRef}
            dir="auto"
            className="chat-message"
            style={{
              padding: isMobile ? "10px 16px" : "10px 16px 24px",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              unicodeBidi: "plaintext",
              textAlign: "start",
              maxHeight: isTruncated
                ? (isExpanded
                  ? `${messageRef.current?.scrollHeight || 1000}px`
                  : "15em")
                : "none",
              overflowX: "auto",
              overflowY: "hidden",
              transition: "max-height 0.35s ease-in-out",
            }}
          >
            {parseHtmlToReactWithCatch(text)}
          </div>
          {isTruncated && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: "4.5em",
                pointerEvents: "none",
                background:
                  `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, ${bubbleBg} 78%)`,
                opacity: isExpanded ? 0 : 1,
                transition: "opacity 0.35s ease-in-out",
              }}
            />
          )}
        </div>
        {translatedText && (
          <div
            dir="auto"
            style={{
              padding: "6px 16px 10px",
              fontSize: "0.92em",
              opacity: 0.85,
              borderTop: `1px solid rgba(255,255,255,0.15)`,
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              fontStyle: "italic",
              unicodeBidi: "plaintext",
              textAlign: "start",
            }}
          >
            {translatedText}
          </div>
        )}
        {!isMobile && isHovered && (
          <div
            className="desktop-only"
            style={{
              position: "absolute",
              bottom: 4,
              right: isLeft ? 8 : "auto",
              left: isLeft ? "auto" : 8,
              display: "flex",
              gap: 4,
              zIndex: 10,
              opacity: 0.7,
              transition: "opacity 0.15s ease",
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.7";
                e.currentTarget.style.background = "transparent";
              }}
              style={{
                background: "transparent",
                border: "none",
                color: bubbleColor,
                opacity: 0.7,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                padding: "4px",
                transition: "all 0.1s ease",
              }}
              title="Copy text"
            >
              <Copy size={13} />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyLink();
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.7";
                e.currentTarget.style.background = "transparent";
              }}
              style={{
                background: "transparent",
                border: "none",
                color: bubbleColor,
                opacity: 0.7,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 4,
                padding: "4px",
                transition: "all 0.1s ease",
              }}
              title="Link to message"
            >
              <Link size={13} />
            </button>
          </div>
        )}
        {isTruncated && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "4px 16px 8px",
              marginTop: -4,
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.background = "rgba(0, 0, 0, 0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.7";
                e.currentTarget.style.background = "transparent";
              }}
              style={{
                background: "transparent",
                border: "none",
                color: bubbleColor,
                opacity: 0.7,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px",
                borderRadius: 4,
                transition: "all 0.1s ease",
              }}
              title={isExpanded ? "View less" : "View more"}
            >
              <div
                style={{
                  display: "inline-flex",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease-in-out",
                }}
              >
                <LucideChevronDown size={14} />
              </div>
            </button>
          </div>
        )}
      </div>

      {showActionSheet && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(3px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            zIndex: 2000,
          }}
          onClick={() => setShowActionSheet(false)}
        >
          <div
            style={{
              background: th.cardBg,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: "16px 16px 24px 16px",
              width: "100%",
              maxWidth: 480,
              boxShadow: "0 -4px 16px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              border: `1px solid ${th.border}`,
              borderBottom: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: 36,
                height: 4,
                background: th.border,
                borderRadius: 2,
                alignSelf: "center",
                marginBottom: 8,
              }}
            />
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: th.textDim,
                textAlign: "center",
                marginBottom: 4,
              }}
            >
              Message Options
            </div>
            <button
              type="button"
              onClick={() => {
                setShowActionSheet(false);
                copyToClipboard();
              }}
              style={{
                background: th.inputBg,
                color: th.textBright,
                border: `1px solid ${th.border}`,
                borderRadius: 12,
                padding: "14px 16px",
                fontSize: 16,
                fontWeight: 500,
                cursor: "pointer",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              Copy Text
            </button>
            <button
              type="button"
              onClick={() => {
                setShowActionSheet(false);
                copyLink();
              }}
              style={{
                background: th.inputBg,
                color: th.textBright,
                border: `1px solid ${th.border}`,
                borderRadius: 12,
                padding: "14px 16px",
                fontSize: 16,
                fontWeight: 500,
                cursor: "pointer",
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              Copy Link to Message
            </button>
            <button
              type="button"
              onClick={() => setShowActionSheet(false)}
              style={{
                background: th.border,
                color: th.textDim,
                border: "none",
                borderRadius: 12,
                padding: "14px 16px",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "center",
                marginTop: 4,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const modulo = (n: number, i: number) => ((i % n) + n) % n;

const relativeConvo = (n: number, current: string) =>
  pipe(
    map(({ id }: ConversationWithParticipants) => id),
    (x: string[]) => x[modulo(x.length, x.indexOf(current) + n)],
  );

const ChevronDown = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const ChevronUp = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const floatingBtnStyle: CSSProperties = {
  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
  borderRadius: 24,
  width: 40,
  height: 40,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  transition: "background 0.15s, color 0.15s",
};

// Generate group chip style with theme-aware colors — hashed by name
const getGroupChipStyle = (name: string): CSSProperties => {
  const color = participantColor(name, [name]);

  return {
    background: color.bg,
    color: color.text,
    borderRadius: 12,
    padding: "2px 8px",
    fontSize: 13,
    fontWeight: 500,
    display: "inline-block",
    maxWidth: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    border: !isDarkMode.value
      ? "1px solid rgba(0,0,0,0.08)"
      : "1px solid rgba(255,255,255,0.15)",
  };
};

export const ConvoMessages = ({
  events,
  hasMoreMessages,
  loadNextPage,
  isLoadingOlder,
  loadError,
  onRetryOlder,
  style,
  conversationId,
  showElapsed,
  translations,
}: {
  events: Message[];
  hasMoreMessages: boolean;
  loadNextPage: () => void;
  isLoadingOlder?: boolean;
  loadError?: { message: string } | Error | null;
  onRetryOlder?: () => void;
  style?: CSSProperties;
  conversationId: string;
  showElapsed: boolean;
  translations?: Map<string, string>;
}) => {
  // Subscribe to theme signal to force re-render when theme changes
  const _theme = isDarkMode.value;

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const hasMoreMessagesRef = useRef(hasMoreMessages);
  hasMoreMessagesRef.current = hasMoreMessages;
  const loadNextPageRef = useRef(loadNextPage);
  loadNextPageRef.current = loadNextPage;
  const isLoadingOlderRef = useRef(isLoadingOlder);
  isLoadingOlderRef.current = isLoadingOlder;
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [showJumpToTop, setShowJumpToTop] = useState(false);
  const activeConvoIdRef = useRef<string | null>(null);
  const hasScrolledInitialRef = useRef(false);
  const scrolledToTargetRef = useRef(false);
  const anchorRef = useRef<{ id: string; offsetTop: number } | null>(null);
  const prevEventsLengthRef = useRef(events.length);
  const prevFirstEventIdRef = useRef<string | null>(events[0]?.id ?? null);
  const cooldownRef = useRef(false);

  const captureScrollAnchor = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const messageElements = container.querySelectorAll(".chat-message");
    for (let i = 0; i < messageElements.length; i++) {
      const el = messageElements[i] as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.bottom > containerRect.top + 2) {
        anchorRef.current = {
          id: el.id,
          offsetTop: rect.top - containerRect.top,
        };
        return;
      }
    }
  }, []);

  // Scroll management: initial scroll, prepending older messages, and sticking to bottom
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !events.length) return;

    const isNewConvo = activeConvoIdRef.current !== conversationId;
    if (isNewConvo) {
      activeConvoIdRef.current = conversationId;
      hasScrolledInitialRef.current = false;
      stickToBottomRef.current = true;
      anchorRef.current = null;
      scrolledToTargetRef.current = false;
    }

    const firstId = events[0]?.id ?? null;

    if (!hasScrolledInitialRef.current) {
      hasScrolledInitialRef.current = true;
      prevEventsLengthRef.current = events.length;
      prevFirstEventIdRef.current = firstId;
      if (!globalThis.location.hash) {
        container.scrollTop = container.scrollHeight;
      }
      return;
    }

    const olderPrepended = events.length > prevEventsLengthRef.current &&
      prevFirstEventIdRef.current !== null &&
      firstId !== prevFirstEventIdRef.current;

    if (olderPrepended) {
      if (anchorRef.current) {
        const anchorEl = document.getElementById(anchorRef.current.id);
        if (anchorEl) {
          const containerRect = container.getBoundingClientRect();
          const newRect = anchorEl.getBoundingClientRect();
          const currentOffset = newRect.top - containerRect.top;
          const diff = currentOffset - anchorRef.current.offsetTop;
          if (Math.abs(diff) > 0.5) {
            container.scrollTop += diff;
          }
        }
        anchorRef.current = null;
      } else if (prevFirstEventIdRef.current) {
        const prevFirstEl = document.getElementById(
          prevFirstEventIdRef.current,
        );
        if (prevFirstEl) {
          prevFirstEl.scrollIntoView({ block: "start" });
        }
      }
    } else if (stickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }

    prevEventsLengthRef.current = events.length;
    prevFirstEventIdRef.current = firstId;
  }, [events, conversationId]);

  // Derive a key that changes when new messages arrive, even if
  // events.length stays the same (e.g. at the message limit).
  const latestEventId = events.length
    ? events.reduce((a, b) =>
      normalizeTimestamp(a.time) > normalizeTimestamp(b.time) ? a : b
    ).id
    : null;

  // Handle scrolling to a deep-linked message from hash
  useEffect(() => {
    if (!events.length) return;
    const hash = globalThis.location.hash;
    if (!hash) return;
    const targetId = hash.slice(1);
    if (!targetId) return;

    if (scrolledToTargetRef.current) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.style.transition = "background-color 0.5s ease";
        element.style.backgroundColor = isDarkMode.value
          ? "rgba(126, 199, 255, 0.25)"
          : "rgba(0, 122, 255, 0.15)";
        setTimeout(() => {
          element.style.backgroundColor = "";
        }, 2500);
        stickToBottomRef.current = false;
        scrolledToTargetRef.current = true;
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [conversationId, events.length]);

  // Effect for scroll event listener and updating stickToBottomRef
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollThresholdBottom = 40;
      const scrollThresholdTop = 100; // px from top edge
      const currentScrollTop = container.scrollTop;
      const currentScrollHeight = container.scrollHeight;
      const currentClientHeight = container.clientHeight;

      const isNearBottom =
        currentScrollHeight - currentScrollTop - currentClientHeight <
          scrollThresholdBottom;
      const isNearTop = currentScrollTop < scrollThresholdTop;

      setShowJumpToBottom(!isNearBottom);
      setShowJumpToTop(currentScrollTop > scrollThresholdBottom);

      // Update stickiness based on scroll position
      stickToBottomRef.current = isNearBottom;

      // Auto-load older messages when scrolling near the top
      const hasHash = !!globalThis.location.hash;
      const canAutoLoad = !hasHash || scrolledToTargetRef.current;
      if (
        isNearTop &&
        hasMoreMessagesRef.current &&
        canAutoLoad &&
        !isLoadingOlderRef.current &&
        !cooldownRef.current
      ) {
        captureScrollAnchor();
        cooldownRef.current = true;
        setTimeout(() => {
          cooldownRef.current = false;
        }, 500);
        loadNextPageRef.current();
      }
    };

    container.addEventListener("scroll", handleScroll);
    // Initial check for scroll state, button visibility, and stickiness
    handleScroll();

    return () => container.removeEventListener("scroll", handleScroll);
  }, [captureScrollAnchor]);

  // Auto-scroll on new messages if user is at bottom
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !events.length) return;
    if (!stickToBottomRef.current) return;
    container.scrollTop = container.scrollHeight;
  }, [latestEventId]);

  const handleJumpToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      stickToBottomRef.current = true;
      setShowJumpToBottom(false);
      setShowJumpToTop(container.scrollTop >= 40);
    }
  };

  const handleJumpToTop = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = 0;
      stickToBottomRef.current = false;
      setShowJumpToTop(false);
      setShowJumpToBottom(
        container.scrollHeight - container.clientHeight >= 40,
      );
    }
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexGrow: 1,
        minHeight: 0,
        ...style,
      }}
    >
      <div
        ref={messagesContainerRef}
        style={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          alignItems: "center",
          overflowY: "auto",
          overflowAnchor: "none",
          scrollbarGutter: "stable",
          minHeight: 0,
          padding: 16,
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 900,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {loadError && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 16px",
                backgroundColor: "rgba(239, 68, 68, 0.12)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                borderRadius: 8,
                margin: "4px 0 8px 0",
                width: "100%",
                boxSizing: "border-box",
                fontSize: 13,
                color: t().text,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  overflow: "hidden",
                }}
              >
                <AlertCircle
                  size={16}
                  color="#ef4444"
                  style={{ flexShrink: 0 }}
                />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Failed to load older messages:{" "}
                  {extractErrorMessage(loadError)}
                </span>
              </div>
              <button
                type="button"
                onClick={onRetryOlder}
                style={{
                  background: primaryColor(),
                  color: "white",
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  flexShrink: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <RefreshCw size={12} />
                Retry
              </button>
            </div>
          )}
          {isLoadingOlder && (
            <div style={{ textAlign: "center", padding: 8, opacity: 0.7 }}>
              <ClipLoader
                color={isDarkMode.value ? "white" : "#007aff"}
                size={22}
              />
            </div>
          )}
          {!isLoadingOlder && !loadError && hasMoreMessages && (
            <div style={{ textAlign: "center", padding: 4 }}>
              <button
                type="button"
                onClick={() => {
                  captureScrollAnchor();
                  loadNextPage();
                }}
                style={{
                  background: "transparent",
                  border: `1px solid ${t().border}`,
                  borderRadius: 6,
                  padding: "4px 14px",
                  fontSize: 12,
                  color: t().textMuted,
                  cursor: "pointer",
                }}
              >
                Load earlier messages
              </button>
            </div>
          )}
          {(() => {
            const sortedEvents = sortKey(
              ({ time }: Message) => normalizeTimestamp(time),
            )(events)
              .filter((e) => e.text.trim().length > 0);

            const uniqueSpeakers = Array.from(
              new Set(events.map((e) => e.from)),
            )
              .sort((a, b) => a.localeCompare(b));

            // Deterministically assign sides to participants to prevent side-flipping
            // when earlier messages are loaded. The most frequent speaker (often the user)
            // goes to flex-end, others go to flex-start.
            const speakerCounts = new Map<string, number>();
            for (const e of events) {
              speakerCounts.set(e.from, (speakerCounts.get(e.from) || 0) + 1);
            }
            const sortedSpeakers = Array.from(speakerCounts.keys()).sort(
              (a, b) => {
                const countDiff = speakerCounts.get(b)! - speakerCounts.get(a)!;
                if (countDiff !== 0) return countDiff;
                return a.localeCompare(b);
              },
            );
            const primarySpeaker = sortedSpeakers[0];

            let previousTime: number | null = null;
            return sortedEvents.map(({ id, text, time, from }) => {
              const normTime = normalizeTimestamp(time);
              const elapsedMs =
                (previousTime === null || previousTime <= 0 || normTime <= 0 ||
                    normTime < previousTime)
                  ? null
                  : normTime - previousTime;
              if (normTime > 0) {
                previousTime = normTime;
              }

              const align = from === primarySpeaker ? "flex-end" : "flex-start";

              return (
                <div
                  className="chat-message"
                  key={id}
                  id={id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: align,
                    marginBottom: 8,
                    width: "100%",
                  }}
                >
                  {elapsedMs !== null &&
                    (showElapsed || elapsedMs >= 10000) && (
                    <div
                      style={{
                        alignSelf: "center",
                        fontSize: 11,
                        color: t().textDim,
                        marginBottom: 6,
                      }}
                    >
                      {formatElapsed(elapsedMs)}
                    </div>
                  )}
                  <div
                    className="message-from"
                    style={{
                      color: participantColor(from, uniqueSpeakers).label,
                      fontWeight: "bold",
                      marginBottom: 3,
                      fontSize: 13,
                      alignSelf: align,
                      opacity: 0.98,
                      letterSpacing: 0.1,
                      cursor: "pointer",
                      userSelect: "all",
                    }}
                    title="Click to copy name"
                    onClick={() => {
                      navigator.clipboard.writeText(from).then(
                        () => toast.success(`Copied: ${from}`),
                        () => toast.error("Failed to copy name"),
                      );
                    }}
                  >
                    <ObfuscatedText text={from} maxLength={30} />
                  </div>
                  <MessageBubble
                    key={`${id}-${isDarkMode.value}`}
                    id={id}
                    text={text}
                    time={normTime}
                    align={align}
                    from={from}
                    allParticipants={uniqueSpeakers}
                    translatedText={translations?.get(id)}
                    conversationId={conversationId}
                  />
                  <div
                    title={normTime > 0 ? new Date(normTime).toString() : ""}
                    style={{
                      fontSize: 11,
                      color: t().textMuted,
                      marginTop: 2,
                      alignSelf: align,
                    }}
                  >
                    <LiveTimeAgo time={normTime} />
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          position: "absolute",
          gap: 12,
          bottom: 16,
          right: 16,
          pointerEvents: "none",
        }}
      >
        <button
          type="button"
          className="compact-button"
          style={{
            visibility: showJumpToTop ? "visible" : "hidden",
            pointerEvents: "auto",
            ...floatingBtnStyle,
          }}
          onClick={handleJumpToTop}
          aria-label="Jump to top"
        >
          <ChevronUp />
        </button>
        <button
          type="button"
          className="compact-button"
          style={{
            visibility: showJumpToBottom ? "visible" : "hidden",
            pointerEvents: "auto",
            ...floatingBtnStyle,
          }}
          onClick={handleJumpToBottom}
          aria-label="Jump to bottom"
        >
          <ChevronDown />
        </button>
      </div>
    </div>
  );
};

const playNotificationSound = () => {
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = 880;
  gain.gain.value = 0.15;
  osc.start();
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
  osc.stop(ctx.currentTime + 0.15);
};

export const ConversationComponent: FC<
  {
    id: string;
    conversationList?: ConversationWithParticipants[];
    onSelectConversation?: (id: string | null) => void;
  }
> = ({ id, conversationList = [], onSelectConversation }) => {
  if (!id?.trim()) {
    return <InvalidIdError id={id} type="conversation" />;
  }
  return (
    <ConversationComponentInner
      id={id.trim()}
      conversationList={conversationList}
      onSelectConversation={onSelectConversation}
    />
  );
};

const ConversationComponentInner: FC<
  {
    id: string;
    conversationList?: ConversationWithParticipants[];
    onSelectConversation?: (id: string | null) => void;
  }
> = ({ id, conversationList = [], onSelectConversation }) => {
  const { route } = useLocation();
  const isMobile = useIsMobile();
  const { isLoading: isAuthLoading, user } = useAuth();

  const convoQueryWhere = isUuid(id) ? { id } : {
    name: { "$ilike": `%${id}%` },
  };

  const { isLoading: isConvoLoading, data: convoData, error: convoError } =
    useQuery({
      conversations: {
        $: { where: convoQueryWhere },
        groups: {
          account: {},
        },
      },
    });

  const convo = convoData?.conversations?.[0];
  const resolvedId = convo?.id ?? id;

  useEffect(() => {
    if (convo?.id && convo.id !== id) {
      onSelectConversation?.(convo.id);
      selectedConversation.value = convo.id;
    }
  }, [convo?.id, id, onSelectConversation]);

  useEffect(() => {
    if (convo && !convos.value.some((c) => c.id === convo.id)) {
      convos.value = [convo as ConversationWithParticipants, ...convos.value];
    }
  }, [convo]);

  useEffect(() => {
    resetParticipantColors();
  }, [resolvedId]);
  const [showElapsed, setShowElapsed] = useState(false);
  const [translations, setTranslations] = useState<Map<string, string>>(
    new Map(),
  );
  const [translating, setTranslating] = useState(false);
  const prevLatestTimeRef = useRef<number | null>(null);
  const [messageLimit, setMessageLimit] = useState(30);
  const [isOlderLoading, setIsOlderLoading] = useState(false);
  const isOlderLoadingRef = useRef(false);

  // Dynamically increase messageLimit if a message is deep-linked in the hash
  useEffect(() => {
    let active = true;

    const handleHashChange = () => {
      const hash = globalThis.location.hash;
      if (!hash) return;
      const targetId = hash.slice(1);
      if (!targetId || !isUuid(targetId)) return;

      queryOnce({
        messages: {
          $: {
            where: { id: targetId },
          },
        },
      }).then((res) => {
        if (!active) return;
        const targetMsg = res.data?.messages?.[0];
        if (!targetMsg) return;

        queryOnce({
          messages: {
            $: {
              where: {
                conversation: resolvedId,
                time: { "$gte": targetMsg.time },
              },
            },
          },
        }).then((res2) => {
          if (!active) return;
          const count = res2.data?.messages?.length ?? 0;
          if (count > 0) {
            setMessageLimit((current) => Math.max(current, count + 15));
          }
        });
      }).catch((err) => {
        console.error("Failed to query target message for deep link:", err);
      });
    };

    handleHashChange();

    if (typeof window !== "undefined") {
      globalThis.addEventListener("hashchange", handleHashChange);
      return () => {
        active = false;
        globalThis.removeEventListener("hashchange", handleHashChange);
      };
    }

    return () => {
      active = false;
    };
  }, [resolvedId]);

  const {
    isLoading: isMessagesLoading,
    data: messagesData,
    error: messagesError,
  } = useQuery({
    messages: {
      $: {
        where: { "conversation": resolvedId },
        limit: messageLimit,
        order: { time: "desc" },
      },
    },
  });

  const rawMessages = messagesData?.messages ?? [];
  const hasMoreMessages = rawMessages.length >= messageLimit;
  const error = messagesError || convoError;
  const isLoading = isMessagesLoading || isConvoLoading || isAuthLoading;

  const cachedMessagesRef = useRef(rawMessages);
  if (nonempty(rawMessages)) cachedMessagesRef.current = rawMessages;
  const messages = nonempty(rawMessages)
    ? rawMessages
    : cachedMessagesRef.current;

  useEffect(() => {
    if (!isMessagesLoading) {
      isOlderLoadingRef.current = false;
      setIsOlderLoading(false);
    }
  }, [isMessagesLoading, messagesData]);

  useEffect(() => {
    if (messagesError) {
      isOlderLoadingRef.current = false;
      setIsOlderLoading(false);
      const msg = extractErrorMessage(messagesError).toLowerCase();
      if (
        msg.includes("timed out") ||
        msg.includes("timeout") ||
        msg.includes("took too long")
      ) {
        if (messages.length > 0 && messageLimit > messages.length) {
          setMessageLimit(Math.max(30, messages.length));
        }
      }
    }
  }, [messagesError, messages.length, messageLimit]);

  const loadNextPage = useCallback(() => {
    if (isOlderLoadingRef.current || isMessagesLoading) return;
    if (!hasMoreMessages) return;
    isOlderLoadingRef.current = true;
    setIsOlderLoading(true);
    setMessageLimit((l) => l + 30);
  }, [isMessagesLoading, hasMoreMessages]);

  const handleRetryOlder = useCallback(() => {
    isOlderLoadingRef.current = true;
    setIsOlderLoading(true);
    setMessageLimit((l) => l + 30);
  }, []);

  if (isLoading && empty(messages)) {
    return <Loading text="Loading conversation..." fullPage />;
  }

  if (!isAuthLoading && !user) {
    return (
      <PageContainer>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            textAlign: "center",
            padding: 24,
            gap: 16,
          }}
        >
          <div style={{ fontSize: 28, fontWeight: 700, color: primaryColor() }}>
            Access Denied
          </div>
          <div style={{ fontSize: 16, color: t().textMuted, maxWidth: 400 }}>
            You must be signed in with an authorized account to view this
            conversation.
          </div>
          <a
            href="/"
            style={{
              background: primaryColor(),
              color: "white",
              padding: "10px 24px",
              borderRadius: 8,
              fontWeight: 600,
              textDecoration: "none",
              marginTop: 8,
            }}
          >
            Sign In
          </a>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    const msg = error.message?.toLowerCase() ?? "";
    const isPermissionError = msg.includes("unauthorized") ||
      msg.includes("permission") ||
      msg.includes("forbidden");

    if (isPermissionError && user) {
      return (
        <PageContainer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              textAlign: "center",
              padding: 24,
              gap: 16,
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 700, color: "#ff4d4f" }}>
              No Permission
            </div>
            <div style={{ fontSize: 16, color: t().textMuted, maxWidth: 450 }}>
              Your account (<strong>{user.email}</strong>) does not have
              permission to view this conversation. Please switch to an
              authorized account or contact the owner.
            </div>
            <a
              href="/"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                border: `1px solid ${t().border}`,
                color: t().textBright,
                padding: "10px 24px",
                borderRadius: 8,
                fontWeight: 600,
                textDecoration: "none",
                marginTop: 8,
              }}
            >
              Go to Home
            </a>
          </div>
        </PageContainer>
      );
    }

    if (empty(messages)) {
      return <ErrorMessage error={error} />;
    }
  }

  const newestMessage = messages[0] || null;

  useEffect(() => {
    if (isMessagesLoading || !newestMessage) return;
    const normTime = normalizeTimestamp(newestMessage.time);
    const isFresh = normTime > 0 && Date.now() - normTime < 5000;
    if (
      isFresh &&
      prevLatestTimeRef.current !== null &&
      normTime > prevLatestTimeRef.current
    ) {
      playNotificationSound();
    }
    // Only strictly increase or set the initial latest time
    if (
      prevLatestTimeRef.current === null ||
      normTime > prevLatestTimeRef.current
    ) {
      prevLatestTimeRef.current = normTime;
    }
  }, [newestMessage?.id, newestMessage?.time, isMessagesLoading]);

  useEffect(() => {
    prevLatestTimeRef.current = null;
    cachedMessagesRef.current = [];
    setMessageLimit(30);
    setTranslations(new Map());
    setTranslating(false);
  }, [resolvedId]);

  if (!isLoading && !convo) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          height: "100%",
          padding: 20,
          textAlign: "center",
          color: t().textMuted,
          background: t().pageBg,
        }}
      >
        <h3 style={{ color: t().textBright, marginBottom: 8 }}>
          Conversation Not Found
        </h3>
        <p style={{ maxWidth: 400, fontSize: 14 }}>
          This conversation may not exist, or you might not have permission to
          view it.
        </p>
      </div>
    );
  }

  const found = conversationList.find((c) => c.id === resolvedId);
  const indexInCollection = found && conversationList.indexOf(found) + 1;

  const accountSecret: string | undefined =
    (convo?.groups?.[0]?.account as any)?.secret ??
      (convo?.groups?.[0]?.account as any)?.[0]?.secret;

  const handleTranslate = useCallback(async () => {
    if (!accountSecret || translating) return;
    if (translations.size > 0) {
      setTranslations(new Map());
      return;
    }
    setTranslating(true);
    try {
      const batch = messages.map((m) => ({ id: m.id, text: m.text }));
      const res = await fetch(getApiBase(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint: "translate",
          token: accountSecret,
          payload: { messages: batch },
        }),
      }).then((r) => r.json());
      if (res.ok && res.translations) {
        const map = new Map<string, string>();
        for (const t of res.translations) map.set(t.id, t.translatedText);
        setTranslations(map);
      } else {
        notifyError(res.error || "Translation failed", "Translation failed");
      }
    } catch (e) {
      notifyError(e, "Translation failed");
    } finally {
      setTranslating(false);
    }
  }, [accountSecret, messages, translating, translations.size]);

  return (
    <div
      style={{
        backgroundColor: t().surfaceBg,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        flex: 1,
        flexGrow: 1,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {nonempty(convo?.groups ?? []) && (
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
            background: t().navBg,
            borderBottom: `1px solid ${t().border}`,
            padding: "8px 10px",
          }}
        >
          {/* Groups this conversation belongs to */}
          {convo!.groups!.map((g, index) => (
            <span
              key={g.id}
              title={g.name}
              style={{ ...getGroupChipStyle(g.name), cursor: "pointer" }}
              onClick={() => {
                selectedGroup.value = g.id;
                selectedConversation.value = null;
                route(`/conversations?groupId=${encodeURIComponent(g.id)}`);
              }}
            >
              {g.name}
            </span>
          ))}

          {/* Mobile navigation buttons - on same line as groups */}
          {isMobile && (
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => {
                  // Navigate back to chat list by clearing conversationId from URL
                  const newParams = new URLSearchParams(
                    globalThis.location.search,
                  );
                  newParams.delete("conversationId");
                  updateUrlParams(newParams, route);
                }}
                style={{
                  background: "transparent",
                  border: `1px solid ${t().inputBorder}`,
                  borderRadius: 6,
                  padding: "4px 10px",
                  color: t().textMuted,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => {
                  // Navigate to filters view by setting view param
                  const newParams = new URLSearchParams(
                    globalThis.location.search,
                  );
                  newParams.set("view", "filter");
                  newParams.delete("conversationId");
                  updateUrlParams(newParams, route);
                }}
                style={{
                  background: "transparent",
                  border: `1px solid ${t().inputBorder}`,
                  borderRadius: 6,
                  padding: "4px 10px",
                  color: t().textMuted,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                <Funnel size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mobile navigation when no groups - show on its own line */}
      {empty(convo?.groups ?? []) && (
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            justifyContent: "flex-end",
            background: t().navBg,
            borderBottom: `1px solid ${t().border}`,
            padding: "8px 10px",
          }}
        >
          <button
            type="button"
            onClick={() => {
              // Navigate back to chat list by clearing conversationId from URL
              const newParams = new URLSearchParams(globalThis.location.search);
              newParams.delete("conversationId");
              updateUrlParams(newParams, route);
            }}
            style={{
              background: "transparent",
              border: `1px solid ${t().inputBorder}`,
              borderRadius: 6,
              padding: "4px 10px",
              color: t().textMuted,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => {
              // Navigate to filters view by setting view param
              const newParams = new URLSearchParams(globalThis.location.search);
              newParams.set("view", "filter");
              newParams.delete("conversationId");
              updateUrlParams(newParams, route);
            }}
            style={{
              background: "transparent",
              border: `1px solid ${t().inputBorder}`,
              borderRadius: 6,
              padding: "4px 10px",
              color: t().textMuted,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            <Funnel size={14} />
          </button>
        </div>
      )}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {isLoading && empty(messages) && <Loading text="Loading messages" />}
        {nonempty(messages) && (
          <ConvoMessages
            hasMoreMessages={hasMoreMessages}
            loadNextPage={loadNextPage}
            isLoadingOlder={isOlderLoading}
            loadError={messagesError}
            onRetryOlder={handleRetryOlder}
            events={messages}
            conversationId={resolvedId}
            showElapsed={showElapsed}
            translations={translations}
            style={{ flex: 1, minHeight: 0, height: "100%" }}
          />
        )}
      </div>
      {nonempty(conversationList) && (
        <div
          style={{
            flexShrink: 0,
            background: t().navBg,
            padding: 10,
            borderTop: `1px solid ${t().border}`,
          }}
        >
          <ConvoControls
            indexInCollection={indexInCollection || 0}
            messages={messages}
            convo={resolvedId}
            accountId={(convo?.groups?.[0]?.account as any)?.id ||
              (convo?.groups?.[0]?.account as any)?.[0]?.id}
            conversationList={conversationList}
            onSelectConversation={onSelectConversation}
            showElapsed={showElapsed}
            setShowElapsed={setShowElapsed}
            onTranslate={handleTranslate}
            translating={translating}
            translated={translations.size > 0}
          />
        </div>
      )}
    </div>
  );
};

const ShareIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);
const CopyIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ConvoControls = ({
  convo,
  accountId,
  messages,
  indexInCollection,
  conversationList,
  onSelectConversation,
  showElapsed,
  setShowElapsed,
  onTranslate,
  translating,
  translated,
}: {
  convo: string;
  accountId: string | undefined;
  messages: Message[];
  indexInCollection: number;
  conversationList: ConversationWithParticipants[];
  onSelectConversation?: (id: string | null) => void;
  showElapsed: boolean;
  setShowElapsed: (value: boolean) => void;
  onTranslate: () => void;
  translating: boolean;
  translated: boolean;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <button
        type="button"
        className="compact-button"
        disabled={empty(conversationList)}
        onClick={() => {
          const next = relativeConvo(-1, convo)(conversationList);
          if (onSelectConversation) onSelectConversation(next);
          else selectedConversation.value = next;
        }}
        aria-label="Previous conversation"
        style={{
          padding: 6,
          minWidth: 0,
          minHeight: 0,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        type="button"
        className="compact-button"
        onClick={() => {
          const url = conversationLink(convo);
          navigator.clipboard.writeText(url).then(
            () => toast.success("Link copied to clipboard!"),
            () => toast.error("Failed to copy link"),
          );
        }}
        aria-label="Share"
        style={{
          padding: 6,
          minWidth: 0,
          minHeight: 0,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ShareIcon />
      </button>
      <button
        type="button"
        className="compact-button"
        onClick={() => {
          const sortedMessages = sortKey(
            ({ time }: Message) => normalizeTimestamp(time),
          )(messages);
          navigator.clipboard.writeText(conversationToText(sortedMessages))
            .then(
              () => toast.success("Conversation copied!"),
              () => toast.error("Failed to copy conversation"),
            );
        }}
        aria-label="Copy text"
        style={{
          padding: 6,
          minWidth: 0,
          minHeight: 0,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CopyIcon />
      </button>
      {nonempty(conversationList) && (
        <div
          style={{
            fontSize: 15,
            color: t().textMuted,
            minWidth: 32,
            textAlign: "center",
          }}
        >
          {indexInCollection || "–"}
          /{conversationList.length}
        </div>
      )}
      <button
        type="button"
        className="compact-button"
        disabled={empty(conversationList)}
        onClick={() => {
          const next = relativeConvo(1, convo)(conversationList);
          if (onSelectConversation) onSelectConversation(next);
          else selectedConversation.value = next;
        }}
        aria-label="Next conversation"
        style={{
          padding: 6,
          minWidth: 0,
          minHeight: 0,
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
      <div ref={menuRef} style={{ position: "relative" }}>
        <button
          type="button"
          className="compact-button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="More options"
          style={{
            padding: 6,
            minWidth: 0,
            minHeight: 0,
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 1,
          }}
        >
          &#x22EE;
        </button>
        {menuOpen && (
          <div
            style={{
              position: "absolute",
              right: 0,
              bottom: "100%",
              marginBottom: 4,
              background: t().cardBg,
              border: `1px solid ${t().borderLight}`,
              borderRadius: 8,
              padding: "8px 12px",
              zIndex: 100,
              whiteSpace: "nowrap",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: t().textMuted,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={showElapsed}
                onChange={(e) => setShowElapsed(e.currentTarget.checked)}
              />
              Show elapsed for all intervals
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: t().textMuted,
                fontSize: 13,
                cursor: "pointer",
                marginTop: 6,
              }}
            >
              <input
                type="checkbox"
                checked={obfuscateIdsSignal.value}
                onChange={(e) => {
                  obfuscateIdsSignal.value = e.currentTarget.checked;
                }}
              />
              Obfuscate User IDs
            </label>
            <div
              style={{
                height: 1,
                background: t().borderLight,
                margin: "8px 0",
              }}
            />
            <button
              type="button"
              disabled={translating}
              style={{
                background: "transparent",
                border: "none",
                color: t().textMuted,
                fontSize: 13,
                cursor: translating ? "wait" : "pointer",
                padding: 0,
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 6,
                width: "100%",
                opacity: translating ? 0.6 : 1,
              }}
              onClick={() => {
                onTranslate();
                setMenuOpen(false);
              }}
            >
              {translating
                ? (
                  <>
                    <ClipLoader size={12} color={t().textMuted} />{" "}
                    Translating...
                  </>
                )
                : translated
                ? "Remove translations"
                : "Translate to English"}
            </button>
            <button
              type="button"
              style={{
                background: "transparent",
                border: "none",
                color: t().textMuted,
                fontSize: 13,
                cursor: "pointer",
                padding: 0,
                textAlign: "left",
                display: "block",
                width: "100%",
                marginTop: 6,
              }}
              onClick={() => {
                const snapshotId = id();
                const sortedMessages = sortKey(
                  ({ time }: Message) => normalizeTimestamp(time),
                )(messages);

                // Anonymize only ID-looking `from` values — keep
                // human-readable names (e.g. "Agent FOMO") as-is.
                const anonMap = new Map<string, string>();
                let counter = 1;
                const anonymized = sortedMessages.map((m) => {
                  if (!looksLikeId(m.from)) return m;
                  if (!anonMap.has(m.from)) {
                    anonMap.set(m.from, `User ${counter++}`);
                  }
                  return { ...m, from: anonMap.get(m.from)! };
                });

                const snapshotUrl =
                  `${globalThis.location.origin}/snapshot/${snapshotId}`;

                const txns = [];
                const updateOp = tx.snapshots[snapshotId].update({
                  createdAt: Date.now(),
                  conversationId: convo,
                  messagesJson: JSON.stringify(anonymized),
                  accountId: accountId || "unknown",
                });

                if (accountId) {
                  txns.push(updateOp.link({ account: accountId }));
                } else {
                  txns.push(updateOp);
                }

                transact(txns);

                navigator.clipboard.writeText(snapshotUrl).then(
                  () => toast.success("Snapshot created & link copied!"),
                  () => toast.error("Failed to copy link"),
                );

                setMenuOpen(false);
              }}
            >
              Share public snapshot
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
